import axios from 'axios';
import { chatApi } from 'utils/api';

export const messageCategories = [
  { key: 'all', label: '전체' },
  { key: 'lesson', label: '수업' },
  { key: 'tour', label: '투어' },
  { key: 'community', label: '커뮤니티' },
  { key: 'support', label: '고객센터' },
];

export const messageTypeMeta = {
  lesson_request: { label: '수업', category: 'lesson', subtitle: '수업 메시지' },
  tour_inquiry: { label: '투어', category: 'tour', subtitle: '투어 메시지' },
  community_dm: { label: '커뮤니티', category: 'community', subtitle: '커뮤니티 메시지' },
  support: { label: '고객센터', category: 'support', subtitle: '고객센터 메시지' },
  system_notice: { label: '안내', category: 'support', subtitle: '안내 메시지' },
};

const roomTypeToMessageType = {
  lesson: 'lesson_request',
  reservation: 'lesson_request',
  tour: 'tour_inquiry',
  community: 'community_dm',
  support: 'support',
  system: 'system_notice',
};

const getCurrentUserId = (userInfo) => {
  const rawId = userInfo?.user_id;
  return rawId == null ? '' : String(rawId);
};

// 채팅 시간 표기 정책: 제품 표시는 항상 한국 시간(KST, Asia/Seoul) 고정이다.
// 브라우저/기기 timezone(UTC, America/New_York 등)에 의존하지 않고, 어느 환경에서 봐도
// 한국 시간 기준으로 동일하게 보이도록 모든 formatter 가 timeZone: 'Asia/Seoul' 을 강제한다.
const DISPLAY_TIME_ZONE = 'Asia/Seoul';

// Chat Backend REST history/list 가 KST wall-clock 시각에 그대로 Z 를 붙여 내려주는 버그 보정용 상수.
// 예: 실제 11:54Z 에 보낸 메시지를 REST history 에서 20:54Z(=+9h)로 반환 → REST 경로에서만 보정한다.
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

// 서버 타임스탬프를 안전하게 Date 로 파싱한다. 모든 경로(소켓 created 이벤트,
// REST history, lastMessageAt, day divider)가 동일한 의미로 해석하도록 단일 정책을 쓴다.
// - Z 나 +/- offset 처럼 명시적 timezone 이 붙은 값은 그 자체로 실제 instant 다.
//   끝이 Z 라는 이유만으로 9시간을 빼는 보정을 하지 않는다(이중 보정 방지).
// - timezone 표기가 없는 "yyyy-mm-dd[ T]hh:mm:ss" 값은 UTC 로 간주해 Z 를 붙인다.
//   iOS/Safari WebView 가 공백 구분 날짜를 파싱하지 못하므로 ISO 의 T 로도 정규화한다.
// - 파싱 불가하면 null 을 돌려 호출부가 원본 문자열을 그대로 노출하지 않게 한다.
// - 없는 값에 현재 시각을 채워 날짜를 지어내지 않는다.
export const parseChatDate = (value, _options = {}) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }
  const text = String(value).trim();
  if (!text) return null;
  const hasExplicitTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
  const normalized = /^\d{4}-\d{2}-\d{2}[ T]/.test(text) ? text.replace(' ', 'T') : text;
  const utcNormalized = !hasExplicitTimezone && /^\d{4}-\d{2}-\d{2}T/.test(normalized) ? `${normalized}Z` : normalized;
  const date = new Date(utcNormalized);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatRestTimestamp = (date) => date.toISOString();

// 현재 REST 버그 패턴은 "KST wall-clock 시각 + Z suffix" 다 (offset 없이 끝에 Z 만 붙음).
// 이 패턴일 때만 -9h 해서 실제 UTC instant 로 되돌린다.
// 명시적 숫자 offset(+09:00, -05:00 등)이 붙은 정상 ISO 는 이미 올바른 instant 이므로 절대 보정하지 않는다.
// TODO(chat-backend): REST history/list 가 send/socket 과 동일한 정상 UTC/offset timestamp 를 반환하게 되면
// 이 compatibility normalization 을 제거해야 한다.
const looksLikeRestSeoulWallClockZ = (value) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z$/i.test(value.trim());

const normalizeRestSeoulWallClockTimestamp = (value) => {
  if (!looksLikeRestSeoulWallClockZ(value)) return value;
  const date = parseChatDate(value);
  if (!date) return value;
  return formatRestTimestamp(new Date(date.getTime() - KST_OFFSET_MS));
};

const normalizeRestMessageTimestamps = (message = {}) => {
  if (!message || typeof message !== 'object') return message;
  return {
    ...message,
    ...(message.createdAt != null ? { createdAt: normalizeRestSeoulWallClockTimestamp(message.createdAt) } : {}),
    ...(message.created_at != null ? { created_at: normalizeRestSeoulWallClockTimestamp(message.created_at) } : {}),
    ...(message.sentAt != null ? { sentAt: normalizeRestSeoulWallClockTimestamp(message.sentAt) } : {}),
    ...(message.sent_at != null ? { sent_at: normalizeRestSeoulWallClockTimestamp(message.sent_at) } : {}),
    ...(message.timestamp != null ? { timestamp: normalizeRestSeoulWallClockTimestamp(message.timestamp) } : {}),
  };
};

const normalizeRestRoomTimestamps = (room = {}) => {
  if (!room || typeof room !== 'object') return room;
  return {
    ...room,
    ...(room.lastMessageAt != null ? { lastMessageAt: normalizeRestSeoulWallClockTimestamp(room.lastMessageAt) } : {}),
    ...(room.last_message_at != null ? { last_message_at: normalizeRestSeoulWallClockTimestamp(room.last_message_at) } : {}),
    ...(room.lastMessage ? { lastMessage: normalizeRestMessageTimestamps(room.lastMessage) } : {}),
    ...(room.last_message ? { last_message: normalizeRestMessageTimestamps(room.last_message) } : {}),
  };
};

// KST 벽시계 구성요소. 한국어 라벨은 Intl 의 한국어 로케일 데이터에 의존하지 않고 직접 조립한다.
//
// 이유: Node 를 small-icu 로 빌드하면 한국어 로케일 데이터가 없어 toLocale*String 이 영어로
// 폴백된다("오후 10:30" 대신 "PM 10:30"). 브라우저는 항상 full-ICU 라 제품 화면에는 차이가
// 없지만, 표기 정책을 런타임과 무관하게 고정해야 CI(Node) 에서도 정책 테스트가 의미를 갖는다.
// 숫자 구성요소만 'en-US'(small-icu 에도 항상 존재)로 뽑고, 한국어 문자열은 아래에서 만든다.
const seoulWallClock = (date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: DISPLAY_TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);
  const valueOf = (type) => Number(parts.find((part) => part.type === type)?.value);
  return {
    year: valueOf('year'),
    month: valueOf('month'),
    day: valueOf('day'),
    // hour12:false 는 자정을 24 로 내는 ICU 버전이 있어 24시간 범위로 접는다.
    hour: valueOf('hour') % 24,
    minute: valueOf('minute'),
  };
};

const KOREAN_WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

// 예: "오후 10:30" / "오전 12:00"(자정) / "오후 12:00"(정오)
const formatSeoulClockLabel = ({ hour, minute }) => {
  const meridiem = hour < 12 ? '오전' : '오후';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${meridiem} ${hour12}:${String(minute).padStart(2, '0')}`;
};

export const formatRelativeTime = (value) => {
  const date = parseChatDate(value);
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute) return '방금 전';
  if (diffMs < hour) return `${Math.max(1, Math.floor(diffMs / minute))}분 전`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}시간 전`;
  if (diffMs < day * 7) return `${Math.floor(diffMs / day)}일 전`;
  const { month, day: dayOfMonth } = seoulWallClock(date);
  return `${month}월 ${dayOfMonth}일`;
};

export const formatMessageTime = (value) => {
  const date = parseChatDate(value);
  if (!date) return '';
  return formatSeoulClockLabel(seoulWallClock(date));
};

// KST(Asia/Seoul) 기준 달력일 키(YYYY-MM-DD). 날짜 divider 그룹핑 및 키 생성에 쓴다.
// 브라우저 timezone 과 무관하게 한국 시간 자정 경계로 묶이도록 timeZone 을 고정한다.
const localDayKey = (date) =>
  new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: DISPLAY_TIME_ZONE,
  }).format(date);

// 카카오톡 스타일 날짜 구분선 라벨. 예: "2026년 6월 8일 월요일" (KST 기준)
export const formatMessageDayLabel = (value) => {
  const date = parseChatDate(value);
  if (!date) return '';
  const { year, month, day } = seoulWallClock(date);
  // 요일도 KST 달력일에서 직접 구해 로케일 데이터에 의존하지 않는다.
  const weekday = KOREAN_WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  return `${year}년 ${month}월 ${day}일 ${weekday}요일`;
};

// 메시지 타임스탬프 필드는 단일 우선순위(createdAt → created_at → sentAt → sent_at → timestamp)로 읽는다.
// send 응답·socket created 이벤트·REST history·list lastMessage 가 모두 같은 필드 순서를 따르도록
// 한 곳에서만 결정해, 경로마다 시간 해석이 어긋나지 않게 한다. camelCase/snake_case 를 함께 허용한다.
// 값이 없으면 null 을 돌려 현재 시각으로 날짜를 지어내지 않는다.
export const pickMessageTimestamp = (message) => {
  if (message == null) return null;
  return (
    message.createdAt ??
    message.created_at ??
    message.sentAt ??
    message.sent_at ??
    message.timestamp ??
    null
  );
};

const messageTimestamp = (message = {}) => pickMessageTimestamp(message?.raw ?? message);

// 같은 타임스탬프일 때 결정적 정렬을 위한 보조 키(id → clientMessageId → client_message_id).
const messageTieKey = (message = {}) =>
  String(message?.id ?? message?.clientMessageId ?? message?.client_message_id ?? '');

// 그룹핑 전 안정적으로 시간순(과거 → 최신) 정렬한다.
// - 히스토리가 역순/뒤섞여 도착해도 day divider 가 시간순으로 찍히도록 방어적으로 정렬한다.
// - 타임스탬프가 없거나 파싱 불가한 메시지는 날짜 있는 메시지 뒤로 보내고, 원래 순서를 유지한다.
// - 타임스탬프가 같으면 tie key(id 등) → 원래 인덱스 순으로 결정적 정렬한다.
const sortMessagesChronologically = (messages) => {
  const decorated = messages.map((message, index) => ({
    message,
    index,
    time: parseChatDate(messageTimestamp(message))?.getTime() ?? null,
  }));

  decorated.sort((a, b) => {
    if (a.time == null && b.time == null) return a.index - b.index;
    if (a.time == null) return 1;
    if (b.time == null) return -1;
    if (a.time !== b.time) return a.time - b.time;
    const tie = messageTieKey(a.message).localeCompare(messageTieKey(b.message));
    if (tie !== 0) return tie;
    return a.index - b.index;
  });

  return decorated.map((entry) => entry.message);
};

// 메시지 목록에 기기 timezone 기준 날짜가 바뀔 때마다 day divider 를 끼워 넣는다.
// - 그룹핑 전에 시간순으로 안정 정렬한다(역순/뒤섞임 방어).
// - 타임스탬프가 없는 메시지는 그대로 두고 구분선을 지어내지 않는다.
// - divider 는 MessageBubble 의 type === 'day' 렌더 경로를 그대로 탄다.
export const groupMessagesByDay = (messages = []) => {
  const timeline = [];
  let lastDayKey = null;
  sortMessagesChronologically(messages).forEach((message) => {
    const date = parseChatDate(messageTimestamp(message));
    if (date) {
      const dayKey = localDayKey(date);
      if (dayKey !== lastDayKey) {
        timeline.push({
          id: `day-${dayKey}`,
          type: 'day',
          from: 'system',
          label: formatMessageDayLabel(date),
        });
        lastDayKey = dayKey;
      }
    }
    timeline.push(message);
  });
  return timeline;
};

const initialsFrom = (value, fallback = 'B') => {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.slice(0, 1).toUpperCase();
};

const typeForRoom = (room = {}) => {
  const sourceType = room.sourceType || room.source_type;
  const roomType = room.roomType || room.room_type;
  return roomTypeToMessageType[sourceType] || roomTypeToMessageType[roomType] || 'community_dm';
};

// 문자열 값을 안전하게 정규화한다.
// - null/undefined, 공백뿐인 문자열, "null"/"undefined" 문자열은 모두 null 로 본다.
// - 그래야 빈 profileImageUrl 이 Avatar 로 흘러가 깨진 <img> 대신 기본 프로필이 뜬다.
const normalizePeerText = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text || text === 'null' || text === 'undefined') return null;
  return text;
};

// DM 방의 상대(peer) 프로필을 정규화한다.
// - directPeer 없음(비-DM) 또는 null(미해결) → null
// - camelCase / snake_case 모두 허용
// - 문자열 필드는 trim/빈값 정리해 빈 닉네임·빈 URL 이 그대로 새지 않게 한다.
const mapDirectPeer = (room = {}) => {
  const directPeer = room.directPeer ?? room.direct_peer;
  if (!directPeer) return null;
  const userId = directPeer.userId ?? directPeer.user_id ?? null;
  return {
    userId: userId === '' ? null : userId,
    nickname: normalizePeerText(directPeer.nickname),
    profileImageUrl: normalizePeerText(directPeer.profileImageUrl ?? directPeer.profile_image_url),
    profileUrl: normalizePeerText(directPeer.profileUrl ?? directPeer.profile_url),
  };
};

export const attachmentKindFromContentType = (contentType = '') => {
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.startsWith('video/')) return 'video';
  return 'file';
};

export const mapChatAttachment = (attachment = {}) => {
  const contentType = attachment.contentType || attachment.content_type || '';
  return {
    id: attachment.id ?? attachment.attachmentId ?? attachment.attachment_id ?? null,
    kind: attachmentKindFromContentType(contentType),
    contentType,
    filename: attachment.filename || attachment.fileName || attachment.name || '첨부파일',
    sizeBytes: Number(attachment.sizeBytes ?? attachment.size_bytes ?? attachment.size ?? 0),
    url: attachment.url || attachment.downloadUrl || attachment.download_url || '',
  };
};

const getMessagePreview = (message = {}, fallback = '아직 메시지가 없습니다.') => {
  const body = String(message?.body ?? '').trim();
  if (body) return body;
  const attachments = message?.attachments || message?.attachment_list || [];
  if (attachments.length > 0) return '첨부파일을 보냈습니다.';
  const messageType = message?.messageType || message?.message_type;
  if (messageType && messageType !== 'text') return '첨부파일을 보냈습니다.';
  return fallback;
};

const getReadState = (message = {}) => {
  if (message.readAt || message.read_at) return true;
  if (typeof message.isRead === 'boolean') return message.isRead;
  if (typeof message.is_read === 'boolean') return message.is_read;
  if (typeof message.read === 'boolean') return message.read;
  return false;
};

export const mapChatMessageToViewMessage = (message = {}, currentUserId = '') => {
  const senderUserId = String(message.senderUserId ?? message.sender_user_id ?? '');
  const isMine = senderUserId && senderUserId === String(currentUserId);
  const body = message.body ?? '';
  const messageType = message.messageType || message.message_type || 'text';
  const attachments = (message.attachments || message.attachment_list || [])
    .map(mapChatAttachment)
    .filter((attachment) => attachment.id != null);

  return {
    id: message.id,
    from: isMine ? 'me' : 'other',
    type: messageType === 'text' ? 'text' : messageType,
    text: body,
    attachments,
    time: formatMessageTime(pickMessageTimestamp(message)),
    read: getReadState(message),
    avatar: initialsFrom(message.senderName || message.senderNickname || body, '상'),
    raw: message,
  };
};

export const mapChatRoomToMessageRoom = (room = {}, { messages = [], userInfo } = {}) => {
  const type = typeForRoom(room);
  const meta = messageTypeMeta[type] || messageTypeMeta.community_dm;
  const peer = mapDirectPeer(room);
  // DM 이면 상대 닉네임을 방 제목/아바타로 우선 사용한다.
  // 서버 title 이 비어 있거나 일반 라벨일 때도 상대 이름이 보이도록 한다.
  const title = peer?.nickname || room.title || meta.label || '메시지';
  const lastMessage = room.lastMessage || room.last_message || null;
  const lastBody = getMessagePreview(lastMessage, room.lastMessageBody || room.last_message_body || '아직 메시지가 없습니다.');
  const lastAt = room.lastMessageAt || room.last_message_at || pickMessageTimestamp(lastMessage);

  return {
    id: String(room.id),
    type,
    avatar: initialsFrom(title),
    title,
    subtitle: room.subtitle || meta.subtitle,
    lastMessageAt: formatRelativeTime(lastAt),
    lastMessage: lastBody,
    unread: Number(room.unreadCount ?? room.unread_count ?? room.unread ?? 0),
    status: meta.label,
    participantMeta: room.participantMeta || room.participant_meta || meta.subtitle,
    pinned: Boolean(room.isPinned ?? room.is_pinned),
    muted: Boolean(room.isMuted ?? room.is_muted),
    archived: Boolean(room.isArchived ?? room.is_archived),
    peer,
    messages: messages.map((message) => mapChatMessageToViewMessage(message, getCurrentUserId(userInfo))),
    raw: room,
  };
};

export const getMessageCategory = (room) => messageTypeMeta[room?.type]?.category || 'community';
export const getMessageTypeLabel = (room) => messageTypeMeta[room?.type]?.label || '메시지';

export const listMessageRooms = async () => {
  const res = await chatApi.get('/chat/api/v1/rooms');
  return (res.data?.rooms || [])
    .map(normalizeRestRoomTimestamps)
    .map((room) => mapChatRoomToMessageRoom(room));
};

export const getMessageRoom = async (roomId, userInfo) => {
  const [roomRes, messagesRes] = await Promise.all([
    chatApi.get(`/chat/api/v1/rooms/${encodeURIComponent(roomId)}`),
    chatApi.get(`/chat/api/v1/rooms/${encodeURIComponent(roomId)}/messages?limit=100`),
  ]);
  return mapChatRoomToMessageRoom(normalizeRestRoomTimestamps(roomRes.data), {
    messages: (messagesRes.data?.messages || []).map(normalizeRestMessageTimestamps),
    userInfo,
  });
};

export const markMessageRoomRead = async (roomId) => {
  const res = await chatApi.patch(`/chat/api/v1/rooms/${encodeURIComponent(roomId)}/read`);
  return res.data;
};

export const sendMessage = async (roomId, body) => {
  const trimmed = String(body || '').trim();
  if (!trimmed) return null;
  const res = await chatApi.post(`/chat/api/v1/rooms/${encodeURIComponent(roomId)}/messages`, {
    body: trimmed,
    clientMessageId: `fe-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  });
  return res.data;
};

export const inferContentTypeFromFilename = (file = {}) => {
  const explicit = String(file.type || '').trim();
  if (explicit) return explicit;
  const name = String(file.name || '').toLowerCase();
  if (/\.jpe?g$/.test(name)) return 'image/jpeg';
  if (/\.png$/.test(name)) return 'image/png';
  if (/\.gif$/.test(name)) return 'image/gif';
  if (/\.webp$/.test(name)) return 'image/webp';
  if (/\.(heic|heif)$/.test(name)) return 'image/heic';
  if (/\.avif$/.test(name)) return 'image/avif';
  if (/\.(mp4|m4v)$/.test(name)) return 'video/mp4';
  if (/\.mov$/.test(name)) return 'video/quicktime';
  if (/\.webm$/.test(name)) return 'video/webm';
  if (/\.avi$/.test(name)) return 'video/x-msvideo';
  if (/\.pdf$/.test(name)) return 'application/pdf';
  return 'application/octet-stream';
};

export const requestAttachmentPresign = async ({ roomId, file }) => {
  const res = await chatApi.post('/chat/api/v1/attachments/presign', {
    roomId: String(roomId),
    filename: file.name,
    contentType: inferContentTypeFromFilename(file),
    sizeBytes: file.size,
  });
  return res.data || {};
};

// Storage(presigned SAS URL)로의 PUT 은 인증 헤더 없이 raw File 만 보낸다.
// Azure Blob Put Blob 은 x-ms-blob-type: BlockBlob 이 필요하며, Content-Type 은 파일 메타데이터로 함께 보낸다.
export const uploadAttachmentBinary = async (uploadUrl, file, { onProgress, signal } = {}) => {
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': inferContentTypeFromFilename(file),
      'x-ms-blob-type': 'BlockBlob',
    },
    signal,
    onUploadProgress: (event) => {
      if (typeof onProgress !== 'function') return;
      const total = event.total || file.size || 0;
      const percent = total > 0 ? Math.round((event.loaded / total) * 100) : 0;
      onProgress(Math.min(100, Math.max(0, percent)));
    },
  });
};

export const uploadAttachment = async ({ roomId, file, onProgress, signal }) => {
  const presign = await requestAttachmentPresign({ roomId, file });
  const uploadUrl = presign.uploadUrl || presign.upload_url;
  const attachmentId = presign.attachmentId || presign.attachment_id;
  if (!uploadUrl || !attachmentId) {
    throw new Error('presign_failed');
  }
  await uploadAttachmentBinary(uploadUrl, file, { onProgress, signal });
  return { ...presign, attachmentId };
};

export const sendMessageWithAttachments = async (roomId, { body, attachmentIds = [] } = {}) => {
  const ids = (attachmentIds || []).filter(Boolean);
  const trimmed = String(body || '').trim();
  if (!trimmed && ids.length === 0) return null;
  const payload = {
    attachmentIds: ids,
    clientMessageId: `fe-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  };
  if (trimmed) payload.body = trimmed;
  const res = await chatApi.post(
    `/chat/api/v1/rooms/${encodeURIComponent(roomId)}/messages/with-attachments`,
    payload,
  );
  return res.data;
};

// disposition 을 넘기면 CHAT 이 그 값을 서명에 반영한 SAS 를 돌려준다.
// - 생략(기본): inline — 사진/영상 인라인 렌더·뷰어 보기용
// - 'attachment': Azure Content-Disposition: attachment + Content-Type 서명 → 실제 다운로드용
export const getAttachmentDownloadUrl = async (attachmentId, { disposition } = {}) => {
  if (!attachmentId) return '';
  const path = `/chat/api/v1/attachments/${encodeURIComponent(attachmentId)}/download-url`;
  const res = disposition
    ? await chatApi.get(path, { params: { disposition } })
    : await chatApi.get(path);
  return res.data?.downloadUrl || res.data?.url || res.data?.download_url || '';
};

export const startDirectMessageRoom = async (participantUserId) => {
  const targetUserId = String(participantUserId || '').trim();
  if (!targetUserId) return null;
  const res = await chatApi.post('/chat/api/v1/rooms/direct', {
    targetUserId,
  });
  const room = res.data?.room || res.data || {};
  if (!room?.id) return null;
  return mapChatRoomToMessageRoom(room);
};
